import { useMutation } from '@tanstack/react-query'
import httpService from './httpService'
import axios from 'axios'

export interface CreateStripeSessionRequest {
  base64ClientKey?: string
  returnUrl?: string
  successUrl?: string
  cancelUrl?: string
  promotionId?: string
  duprProductId?: string
}

export interface GetActivePromotionProductsResponse {
  duprProducts?: Array<DuprProduct>
  products?: Array<DuprProduct>
  properties?: Record<string, string>
}

export interface GetActivePromotionProductsRequest {
  base64ClientKey: string
  promotionId: string
}

export interface PromotionProductsResult {
  products: DuprProduct[]
  properties: Record<string, string>
}

export interface CreateStripeSessionResponse {
  clientSecret: string
  sessionId: string
}

export interface Benefactor {
  id: number
  displayName: string
}

export interface DuprDiscount {
  priceReduction: number
  trialDays: number
}

export interface Promotion {
  duprDiscount: DuprDiscount
  benefactor: Benefactor
}

export interface DuprProduct {
  productId: string
  externalProductId: string
  priceId: string
  displayName: string
  price: number
  period: string
  promotion: Promotion
  properties?: Record<string, string>
}

export interface GetEligibilePromotionsRequest {
  base64ClientKey: string
}

export const oauthCheckProductEligibility = async (request: {
  token: string
  request: GetEligibilePromotionsRequest
}): Promise<string[]> => {
  return await axios
    .create()
    .post<Result<string[]>>('/subscription/promotions/list', request.request, {
      headers: { Authorization: `Bearer ${request.token}` },
    })
    .then((res) =>
      res.data.results.reduce((l: string[], r: string[]) => {
        return [...l, ...r]
      })
    )
    .catch((error) => {
      // Log the error for debugging purposes
      console.error('Failed to check product eligibility:', error)

      if (error?.response) {
        console.error('API Error details:', {
          status: error.response.status,
          data: error.response.data,
          url: '/subscription/promotions/list',
        })
      }

      // Throw the error so calling code can handle it appropriately
      throw error
    })
}

/**
 * User-scoped view of a promotion the authenticated user is eligible for, returned by
 * POST /subscription/promotions/eligible (no client key). `rcOfferingId` (when present) is the
 * RevenueCat offering id override the purchase should be attributed to — mirrors the override the
 * external/premium sign-up flow reads from a promotion's `rc_offering_id` property.
 */
export interface EligiblePromotionView {
  promotionId: string
  name: string
  superwallPlacementId?: string | null
  rcOfferingId?: string | null
}

/**
 * Fetches the authenticated user's eligible promotions (priority order). Uses the app session
 * token via httpService. Returns [] on any failure so callers can safely fall back to the default
 * RevenueCat offering.
 */
export const getEligiblePromotionsForUser = async (): Promise<EligiblePromotionView[]> => {
  try {
    const res = (await httpService.Post(
      '/subscription/promotions/eligible',
      undefined
    )) as Result<EligiblePromotionView>
    if (res.status !== 'SUCCESS' || !res.results) return []
    return res.results
  } catch (error) {
    console.error('Failed to fetch eligible promotions for user:', error)
    return []
  }
}

export const useRegisterEventWithPayment = () => {
  return useMutation([], async (payload: any) => {
    const res = await httpService.Post(`event/${payload?.eventId}/v1.1/checkout`, payload?.payload)
    return res as any
  })
}

export interface Error {
  message: string
}

export interface Result<T> {
  status: string
  results: Array<T>
  errors: Array<Error>
}

export const oauthGetProductsInPromotion = async (request: {
  token: string
  request: GetActivePromotionProductsRequest
}): Promise<PromotionProductsResult> => {
  return await axios
    .create()
    .post<Result<GetActivePromotionProductsResponse>>('/subscription/promotions/byPromotionId', request.request, {
      headers: { Authorization: `Bearer ${request.token}` },
    })
    .then((res) => {
      // Check if the API returned a failed status or no results
      if (res.data.status !== 'SUCCESS' || !res.data.results || res.data.results.length === 0) {
        return { products: [], properties: {} }
      }

      const firstResult = res.data.results[0]

      // Extract products from results, handling both 'duprProducts' and 'products' field names
      const allProducts = res.data.results
        .map((result) => {
          // Handle both response structures: duprProducts or products
          return result.duprProducts || result.products || []
        })
        .flat()

      // Extract promotion-level properties from the first result
      const properties = firstResult?.properties || {}

      return {
        products: allProducts,
        properties,
      }
    })
}
export const oauthCheckoutSessionCreate = //= () => {
  async (request: { token: string; stripeRequest: CreateStripeSessionRequest }) => {
    return await axios
      .create()
      .put<CreateStripeSessionResponse>('/subscription/checkout/session', request.stripeRequest, {
        headers: { Authorization: `Bearer ${request.token}` },
      })
      .then((res) => res.data)
    //const res = await httpService.Post(activeSubscriptions, undefined)
    //return res as SubscriptionResponse
  }

export const oauthCheckoutSessionStatus = async (request: { token: string; sessionId: string }) => {
  return await axios
    .create()
    .post<{ sessionId: string; status: string; paymentStatus: string | null }>(
      '/subscription/checkout/session/status',
      { sessionId: request.sessionId },
      { headers: { Authorization: `Bearer ${request.token}` } }
    )
    .then((res) => res.data)
}

export const useRegisterEvent = () => {
  return useMutation([], async (payload: any) => {
    const res = await httpService.Post(`event/${payload?.eventId}/v1.1/join`, payload?.payload)
    return res as any
  })
}

export const useRegisterEventWithPaymentFromPlayerBrackets = () => {
  return useMutation([], async (eventId: string) => {
    const res = await httpService.Get<any>(`event/${eventId}/v1.0/checkout`)
    return res
  })
}
